import React, { useState } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Button, 
  Typography, 
  Form, 
  InputNumber, 
  Select,
  Switch,
  message,
  Divider,
  Space,
  Alert,
  Tag,
  Collapse,
  List
} from 'antd';
import { 
  SettingOutlined,
  SaveOutlined,
  ReloadOutlined,
  CreditCardOutlined,
  DollarOutlined,
  GiftOutlined,
  ExperimentOutlined,
  QuestionCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { SubscriptionConfig, adminApi } from '../lib/api';

const { Title, Text } = Typography;
const { Option } = Select;
const { Panel } = Collapse;

const SubscriptionConfigComponent: React.FC = () => {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const [hasChanges, setHasChanges] = useState(false);

  const {
    data: config,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['subscription-config'],
    queryFn: () => adminApi.getSubscriptionConfig(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const updateConfigMutation = useMutation({
    mutationFn: (updatedConfig: Partial<SubscriptionConfig>) => 
      adminApi.updateSubscriptionConfig(updatedConfig),
    onSuccess: () => {
      message.success('Configuration updated successfully');
      queryClient.invalidateQueries({ queryKey: ['subscription-config'] });
      setHasChanges(false);
    },
    onError: (error: any) => {
      message.error('Failed to update configuration: ' + (error.message || 'Unknown error'));
    }
  });

  React.useEffect(() => {
    if (config) {
      form.setFieldsValue(config);
      setHasChanges(false);
    }
  }, [config, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      updateConfigMutation.mutate(values);
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const handleReset = () => {
    if (config) {
      form.setFieldsValue(config);
      setHasChanges(false);
    }
  };

  const handleFormChange = () => {
    setHasChanges(true);
  };

  if (error) {
    return (
      <Alert
        message="Error Loading Configuration"
        description="Failed to load subscription configuration. Please try again."
        type="error"
        action={
          <Button size="small" onClick={() => refetch()}>
            <ReloadOutlined /> Retry
          </Button>
        }
      />
    );
  }

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
        <Col span={24}>
          <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
            <div>
              <Title level={3} style={{ margin: 0 }}>Subscription Configuration</Title>
              <Text type="secondary">Manage payment methods, currencies, and subscription settings</Text>
            </div>
            <Space>
              <Button 
                icon={<ReloadOutlined />}
                onClick={() => refetch()}
                loading={isLoading}
              >
                Refresh
              </Button>
              {hasChanges && (
                <>
                  <Button onClick={handleReset}>
                    Reset Changes
                  </Button>
                  <Button 
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={handleSave}
                    loading={updateConfigMutation.isPending}
                  >
                    Save Changes
                  </Button>
                </>
              )}
            </Space>
          </Space>
        </Col>
      </Row>

      {hasChanges && (
        <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
          <Col span={24}>
            <Alert
              message="You have unsaved changes"
              description="Remember to save your changes before leaving this page."
              type="warning"
              showIcon
              closable
            />
          </Col>
        </Row>
      )}

      <Form
        form={form}
        layout="vertical"
        onValuesChange={handleFormChange}
        initialValues={config}
      >
        <Collapse defaultActiveKey={['payment', 'currency']} ghost>
          {/* Payment Methods Configuration */}
          <Panel 
            header={
              <Space>
                <CreditCardOutlined />
                <span>Payment Methods</span>
              </Space>
            } 
            key="payment"
          >
            <Card>
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={8}>
                  <Form.Item
                    label={
                      <Space>
                        <Text>Stripe Integration</Text>
                        <Tag color={config?.payment_methods?.stripe_enabled ? 'success' : 'default'}>
                          {config?.payment_methods?.stripe_enabled ? 'Enabled' : 'Disabled'}
                        </Tag>
                      </Space>
                    }
                    name={['payment_methods', 'stripe_enabled']}
                    valuePropName="checked"
                  >
                    <Switch 
                      checkedChildren={<CheckCircleOutlined />}
                      unCheckedChildren={<CloseCircleOutlined />}
                    />
                  </Form.Item>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Enable Stripe for credit card payments
                  </Text>
                </Col>
                
                <Col xs={24} sm={8}>
                  <Form.Item
                    label={
                      <Space>
                        <Text>NOWPayments (Crypto)</Text>
                        <Tag color={config?.payment_methods?.nowpayments_enabled ? 'success' : 'default'}>
                          {config?.payment_methods?.nowpayments_enabled ? 'Enabled' : 'Disabled'}
                        </Tag>
                      </Space>
                    }
                    name={['payment_methods', 'nowpayments_enabled']}
                    valuePropName="checked"
                  >
                    <Switch 
                      checkedChildren={<CheckCircleOutlined />}
                      unCheckedChildren={<CloseCircleOutlined />}
                    />
                  </Form.Item>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Enable cryptocurrency payments
                  </Text>
                </Col>
                
                <Col xs={24} sm={8}>
                  <Form.Item
                    label={
                      <Space>
                        <Text>PayPal Integration</Text>
                        <Tag color={config?.payment_methods?.paypal_enabled ? 'success' : 'default'}>
                          {config?.payment_methods?.paypal_enabled ? 'Enabled' : 'Disabled'}
                        </Tag>
                      </Space>
                    }
                    name={['payment_methods', 'paypal_enabled']}
                    valuePropName="checked"
                  >
                    <Switch 
                      checkedChildren={<CheckCircleOutlined />}
                      unCheckedChildren={<CloseCircleOutlined />}
                    />
                  </Form.Item>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Enable PayPal payments
                  </Text>
                </Col>
              </Row>
            </Card>
          </Panel>

          {/* Currency Configuration */}
          <Panel 
            header={
              <Space>
                <DollarOutlined />
                <span>Currency Settings</span>
              </Space>
            } 
            key="currency"
          >
            <Card>
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Primary Currency"
                    name={['currencies', 'primary']}
                    rules={[{ required: true, message: 'Please select primary currency' }]}
                  >
                    <Select>
                      <Option value="USD">USD - US Dollar</Option>
                      <Option value="EUR">EUR - Euro</Option>
                      <Option value="GBP">GBP - British Pound</Option>
                      <Option value="CAD">CAD - Canadian Dollar</Option>
                      <Option value="AUD">AUD - Australian Dollar</Option>
                    </Select>
                  </Form.Item>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Primary currency for pricing and billing
                  </Text>
                </Col>
                
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Supported Currencies"
                    name={['currencies', 'supported']}
                    rules={[{ required: true, message: 'Please select supported currencies' }]}
                  >
                    <Select
                      mode="multiple"
                      placeholder="Select currencies"
                    >
                      <Option value="USD">USD - US Dollar</Option>
                      <Option value="EUR">EUR - Euro</Option>
                      <Option value="GBP">GBP - British Pound</Option>
                      <Option value="CAD">CAD - Canadian Dollar</Option>
                      <Option value="AUD">AUD - Australian Dollar</Option>
                      <Option value="JPY">JPY - Japanese Yen</Option>
                    </Select>
                  </Form.Item>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Additional currencies supported for payments
                  </Text>
                </Col>
              </Row>
            </Card>
          </Panel>

          {/* Trial Settings */}
          <Panel 
            header={
              <Space>
                <ExperimentOutlined />
                <span>Trial Settings</span>
              </Space>
            } 
            key="trial"
          >
            <Card>
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Trial Duration (Days)"
                    name={['trial_settings', 'trial_duration_days']}
                    rules={[{ type: 'number', min: 0, max: 365 }]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      max={365}
                      placeholder="e.g., 7, 14, 30"
                    />
                  </Form.Item>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Number of days for free trial period
                  </Text>
                </Col>
                
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Trial Features"
                    name={['trial_settings', 'trial_features']}
                  >
                    <Select
                      mode="multiple"
                      placeholder="Select trial features"
                    >
                      <Option value="bronze">Bronze Features</Option>
                      <Option value="silver">Silver Features</Option>
                      <Option value="gold">Gold Features</Option>
                    </Select>
                  </Form.Item>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Features available during trial period
                  </Text>
                </Col>
              </Row>

              <Divider />
              
              <Space direction="vertical" style={{ width: '100%' }}>
                <Title level={5}>Trial Configuration Guidelines</Title>
                <List
                  size="small"
                  dataSource={[
                    'Recommended trial duration: 7-14 days for optimal conversion',
                    'Trial should include meaningful features to demonstrate value',
                    'Users should be able to experience core functionality',
                    'Clear upgrade path should be presented during trial'
                  ]}
                  renderItem={(item) => (
                    <List.Item>
                      <Text style={{ fontSize: '12px' }}>
                        <QuestionCircleOutlined style={{ color: '#1890ff', marginRight: '4px' }} />
                        {item}
                      </Text>
                    </List.Item>
                  )}
                />
              </Space>
            </Card>
          </Panel>

          {/* Referral Settings */}
          <Panel 
            header={
              <Space>
                <GiftOutlined />
                <span>Referral Program</span>
              </Space>
            } 
            key="referral"
          >
            <Card>
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Enable Referral Program"
                    name={['referral_settings', 'enabled']}
                    valuePropName="checked"
                  >
                    <Switch 
                      checkedChildren="Enabled"
                      unCheckedChildren="Disabled"
                    />
                  </Form.Item>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Allow users to refer friends for rewards
                  </Text>
                </Col>
                
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Reward Duration (Days)"
                    name={['referral_settings', 'reward_days']}
                    rules={[{ type: 'number', min: 0, max: 365 }]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      max={365}
                      placeholder="e.g., 30, 60, 90"
                      disabled={!config?.referral_settings?.enabled}
                    />
                  </Form.Item>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Days of free premium access for successful referrals
                  </Text>
                </Col>
              </Row>

              <Divider />
              
              <Alert
                message="Referral Program Benefits"
                description={
                  <ul style={{ marginBottom: 0, paddingLeft: '20px' }}>
                    <li>Increases user acquisition through word-of-mouth</li>
                    <li>Provides value to existing users through rewards</li>
                    <li>Creates viral growth loop for sustainable expansion</li>
                    <li>Cost-effective compared to traditional advertising</li>
                  </ul>
                }
                type="info"
                showIcon
              />
            </Card>
          </Panel>

          {/* System Settings */}
          <Panel 
            header={
              <Space>
                <SettingOutlined />
                <span>System Settings</span>
              </Space>
            } 
            key="system"
          >
            <Card>
              <Alert
                message="Advanced Configuration"
                description="These settings affect core subscription functionality. Please test thoroughly in a staging environment before applying to production."
                type="warning"
                showIcon
                style={{ marginBottom: '16px' }}
              />
              
              <Row gutter={[16, 16]}>
                <Col span={24}>
                  <Title level={5}>Subscription Lifecycle</Title>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Text>
                      <strong>Grace Period:</strong> 3 days after payment failure before downgrade
                    </Text>
                    <Text>
                      <strong>Cancellation:</strong> Access continues until end of billing period
                    </Text>
                    <Text>
                      <strong>Upgrade:</strong> Immediate access with prorated billing
                    </Text>
                    <Text>
                      <strong>Downgrade:</strong> Takes effect at next billing cycle
                    </Text>
                  </Space>
                </Col>
              </Row>
            </Card>
          </Panel>
        </Collapse>
      </Form>

      {/* Save Changes Footer */}
      {hasChanges && (
        <Card style={{ marginTop: '16px', background: '#fafafa' }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Space>
                <ExclamationCircleOutlined style={{ color: '#fa8c16' }} />
                <Text>You have unsaved changes</Text>
              </Space>
            </Col>
            <Col>
              <Space>
                <Button onClick={handleReset}>
                  Cancel Changes
                </Button>
                <Button 
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={handleSave}
                  loading={updateConfigMutation.isPending}
                >
                  Save Configuration
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>
      )}
    </div>
  );
};

export default SubscriptionConfigComponent;